/**
 * Impersonation session state helpers.
 *
 * While an admin is impersonating a customer on the headless storefront, the SPA
 * stores impersonation metadata alongside the impersonation JWT (which lives in
 * the standard `auth_token` slot so the shared ApiClient picks it up). This module
 * reads/writes/clears that metadata and answers "is the current session an
 * impersonation session?".
 *
 * Kept as plain functions (not React state) so the banner, the /impersonate page,
 * and route guards all observe the same source of truth; the banner listens to the
 * `impersonationChange` window event to re-render on updates.
 */

import { STORAGE_KEYS } from "@/lib/constants/storageKeys";

export interface ImpersonationSession {
  /** Target (impersonated) customer Id. */
  targetCustomerId: number;
  /** Display name shown in the banner. */
  targetDisplayName: string;
  /** Target email shown in the banner. */
  targetEmail: string;
  /** Admin customer Id that started the session. */
  adminId: number;
  /**
   * Absolute base URL of the admin area as observed by the Start endpoint.
   * Finish navigates the top-level window back to
   * `${adminBaseUrl}Admin/Customer/Edit/${targetCustomerId}`.
   */
  adminBaseUrl: string;
  /** Unix-seconds expiry of the impersonation JWT. */
  expiresAt: number;
}

export const IMPERSONATION_CHANGE_EVENT = "impersonationChange";

const isBrowser = (): boolean => typeof window !== "undefined";

export const getImpersonationSession = (): ImpersonationSession | null => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.impersonation);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationSession;
    if (!parsed || typeof parsed.targetCustomerId !== "number" || typeof parsed.adminId !== "number") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const setImpersonationSession = (session: ImpersonationSession): void => {
  if (!isBrowser()) return;
  window.localStorage.setItem(STORAGE_KEYS.impersonation, JSON.stringify(session));
  window.dispatchEvent(new Event(IMPERSONATION_CHANGE_EVENT));
};

export const clearImpersonationSession = (): void => {
  if (!isBrowser()) return;
  window.localStorage.removeItem(STORAGE_KEYS.impersonation);
  window.dispatchEvent(new Event(IMPERSONATION_CHANGE_EVENT));
};

/**
 * True when the stored session exists AND its token window has not elapsed.
 * An expired session is treated as not-active for permissions purposes, but the
 * banner reads the raw session to show the "expired" state.
 */
export const isImpersonationActive = (): boolean => {
  const session = getImpersonationSession();
  if (!session) return false;
  return Date.now() / 1000 < session.expiresAt;
};